import {action, makeObservable, observable} from 'mobx'
import {injectable} from 'inversify'
import {subscribe} from 'dna-react-ioc'

import {OnboardingStore} from './OnboardingStore'
import {ONBOARDING_STORE_SYMBOLS} from './symbols'
import {VerificationResponse} from '@/stores/onboarding/models/onboarding'
import {DocumentInfo} from '@/stores/onboarding/models/onboarding'
import {onboardingMessage} from '@/pages/onboarding/utils'

@injectable()
export class OnboardingAddPersonStore {
    isVerifying = false
    isVerified = false
    verificationResponse: VerificationResponse | null = null
    verificationFailed = false
    needsDocumentUpload = false
    uploadedFiles: DocumentInfo[] = []
    isEditMode = false
    showEditConfirmModal = false
    preserveVerificationState = false

    private onboardingStore: OnboardingStore

    constructor() {
        this.onboardingStore = subscribe<OnboardingStore>(ONBOARDING_STORE_SYMBOLS.OnboardingStore)

        makeObservable(this, {
            isVerifying: observable,
            isVerified: observable,
            verificationResponse: observable,
            verificationFailed: observable,
            needsDocumentUpload: observable,
            uploadedFiles: observable,
            isEditMode: observable,
            showEditConfirmModal: observable,
            preserveVerificationState: observable,
            setVerifying: action,
            setVerified: action,
            setVerificationResponse: action,
            setVerificationFailed: action,
            setNeedsDocumentUpload: action,
            setUploadedFiles: action,
            setEditMode: action,
            setShowEditConfirmModal: action,
            resetState: action,
            setPreserveVerificationState: action,
            initializeWithPersonData: action
        })
    }

    setVerifying(isVerifying: boolean): void {
        this.isVerifying = isVerifying
    }

    setVerified(isVerified: boolean): void {
        this.isVerified = isVerified
    }

    setVerificationResponse(response: VerificationResponse | null): void {
        this.verificationResponse = response
    }

    setVerificationFailed(failed: boolean): void {
        this.verificationFailed = failed
    }

    setNeedsDocumentUpload(needs: boolean): void {
        this.needsDocumentUpload = needs
    }

    setUploadedFiles(files: DocumentInfo[]): void {
        this.uploadedFiles = files
    }

    setEditMode(isEditMode: boolean): void {
        this.isEditMode = isEditMode
    }

    setShowEditConfirmModal(show: boolean): void {
        this.showEditConfirmModal = show
    }

    resetState(): void {
        // Don't reset verification state if it should be preserved
        if (!this.preserveVerificationState) {
            this.isVerified = false
            this.verificationResponse = null
            this.verificationFailed = false
            this.needsDocumentUpload = false
            this.uploadedFiles = []
        }
        this.isEditMode = false
        this.showEditConfirmModal = false
    }

    setPreserveVerificationState(preserve: boolean): void {
        this.preserveVerificationState = preserve
    }

    initializeWithPersonData(personData: any): void {
        if (!personData) return

        if (personData.documents && personData.documents.length > 0) {
            this.setUploadedFiles(personData.documents)
            this.setVerified(true)
            this.setVerificationFailed(true)
        } else if (personData.uploadDocManually && personData.isVerified) {
            this.setVerified(true)
            this.setVerificationFailed(true)
        } else if (
            personData.isVerified === true &&
            (personData.verificationResponse?.isVerified === true ||
                personData.officerVerificationResult?.isVerified === true)
        ) {
            this.setVerified(true)
            this.setVerificationFailed(false)
            this.setVerificationResponse(
                personData.verificationResponse || personData.officerVerificationResult
            )
        }
    }

    async verifyPerson(
        values: any,
        type: 'business-owner' | 'director' | 'individual-shareholder'
    ): Promise<void> {
        try {
            this.setVerifying(true)

            const transformedValues = {
                ...values,
                address: {
                    postalCode: values.postalCode || '',
                    addressLine1: values.addressLine1 || '',
                    addressLine2: values.addressLine2 || '',
                    townOrCity: values.townOrCity || ''
                }
            }

            const result = await this.onboardingStore.verifyPerson(transformedValues, type)

            if (result.verified) {
                this.setVerified(true)
                this.setVerificationResponse(result.verificationResponse as VerificationResponse)
                this.setVerificationFailed(false)
                this.setNeedsDocumentUpload(false)
                onboardingMessage.success('Person verified successfully')
            } else {
                this.setVerificationFailed(true)
                this.setVerificationResponse(result.verificationResponse as VerificationResponse)

                const response = result.verificationResponse

                if (
                    response &&
                    (!response.isVerified ||
                        response.addressVerifications?.some((v: any) => v.verified === false) ||
                        response.idVerifications?.some((v: any) => v.verified === false))
                ) {
                    this.setNeedsDocumentUpload(true)
                    onboardingMessage.warning(
                        'Person verification failed. Please upload supporting documents.'
                    )
                } else {
                    onboardingMessage.error('Person verification failed')
                }
            }
        } catch (error) {
            console.error('Person verification failed:', error)
            onboardingMessage.error('Person verification failed')
        } finally {
            this.setVerifying(false)
            this.setEditMode(false)
        }
    }

    async handleDocumentsUploaded(
        documents: DocumentInfo[],
        personId?: string,
        type?: 'business-owner' | 'director' | 'individual-shareholder'
    ): Promise<void> {
        this.setUploadedFiles(documents)
        this.setNeedsDocumentUpload(false)
        this.setVerified(true)

        // Update the person record with the uploaded documents
        if (personId && type) {
            try {
                const personData = {
                    documents: documents,
                    uploadDocManually: true,
                    isVerified: true,
                    verificationFailed: false
                }

                if (type === 'director') {
                    await this.onboardingStore.updateDirector(personId, personData)
                } else if (type === 'business-owner' || type === 'individual-shareholder') {
                    await this.onboardingStore.updateOwner(personId, personData)
                }
            } catch (error) {
                console.error('Failed to update person with documents:', error)
            }
        }

        onboardingMessage.success('Documents uploaded successfully. You can now continue.')
    }

    async handleRemoveDocument(
        documentIndex: number,
        personId?: string,
        type?: 'business-owner' | 'director' | 'individual-shareholder'
    ): Promise<void> {
        const updatedFiles = this.uploadedFiles.filter((_, index) => index !== documentIndex)
        this.setUploadedFiles(updatedFiles)

        if (personId) {
            try {
                const personData = {
                    documents: updatedFiles,
                    uploadDocManually: updatedFiles.length > 0,
                    isVerified: updatedFiles.length > 0,
                    verificationFailed: updatedFiles.length > 0
                }

                if (type === 'director') {
                    await this.onboardingStore.updateDirector(personId, personData)
                } else if (type === 'business-owner' || type === 'individual-shareholder') {
                    await this.onboardingStore.updateOwner(personId, personData)
                }
            } catch (error) {
                console.error('Failed to update documents for person:', error)
            }
        }

        if (updatedFiles.length === 0) {
            this.setVerified(false)
            this.setVerificationFailed(false)
            this.setNeedsDocumentUpload(false)
        }

        onboardingMessage.success('Document removed successfully.')
    }

    handleEditClick(): void {
        this.setShowEditConfirmModal(true)
    }

    async handleEditConfirm(
        personId?: string,
        type?: 'business-owner' | 'director' | 'individual-shareholder'
    ): Promise<void> {
        this.setEditMode(true)
        this.setVerified(false)
        this.setVerificationFailed(false)
        this.setShowEditConfirmModal(false)
        this.setVerificationResponse(null)
        this.setUploadedFiles([])

        if (personId) {
            if (type === 'director') {
                const director = this.onboardingStore.directors.find((d) => d.id === personId)
                if (director) {
                    director.documents = []
                    director.uploadDocManually = false
                    director.isVerified = false
                    director.verificationFailed = false
                    director.verificationResponse = null
                    director.officerVerificationResult = null
                }
            } else if (type === 'business-owner' || type === 'individual-shareholder') {
                const owner = this.onboardingStore.owners.find((o) => o.id === personId)
                if (owner) {
                    owner.documents = []
                    owner.uploadDocManually = false
                    owner.isVerified = false
                    owner.verificationFailed = false
                    owner.verificationResponse = null
                    owner.officerVerificationResult = null
                }
            }
        }
    }

    handleEditCancel(): void {
        this.setShowEditConfirmModal(false)
    }

    isFieldDisabled(fieldName: string, personData: any, initialValues: any): boolean {
        const isPersonVerified = personData?.isVerified === true

        if (isPersonVerified && !this.isEditMode) {
            return true
        }

        if (personData?.preFilledFromApi?.[fieldName] !== true) {
            return false
        }

        if (fieldName === 'residentialAddress') {
            const address = personData?.address || personData?.residentialAddress
            return !!(address?.postalCode || address?.addressLine1 || address?.townOrCity)
        }

        const fieldValue = personData?.[fieldName] || initialValues?.[fieldName]
        return !!(fieldValue && fieldValue.toString().trim())
    }

    shouldShowEditButton(personData: any): boolean {
        return personData?.isVerified === true && !this.isEditMode
    }
}
